import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  CounterBadge,
  Divider,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import { AlertRegular, BugRegular, CheckmarkCircleRegular, LightbulbRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api/useApi';
import type { NotificationItemDto, NotificationsDto } from '../api/types';

const REFRESH_MS = 20_000;

function ago(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'gerade eben';
  if (s < 3600) return `vor ${Math.floor(s / 60)} Min`;
  if (s < 86400) return `vor ${Math.floor(s / 3600)} Std`;
  return `vor ${Math.floor(s / 86400)} T`;
}

export function NotificationBell() {
  const api = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState<NotificationsDto>({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api
      .getNotifications()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, REFRESH_MS);
    // „Live" genug: sofort aktualisieren, wenn der Tab wieder in den Fokus kommt.
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  async function onOpenChange(next: boolean) {
    setOpen(next);
    if (next && data.unread > 0) {
      try {
        await api.markNotificationsSeen();
        setData((d) => ({ ...d, unread: 0 }));
      } catch {
        /* ignore */
      }
    }
  }

  function activate(item: NotificationItemDto) {
    setOpen(false);
    if (item.external) {
      window.open(item.url, '_blank', 'noopener');
    } else {
      navigate(item.url);
    }
  }

  function icon(item: NotificationItemDto) {
    if (item.kind === 'approval') return <CheckmarkCircleRegular />;
    return item.subtitle?.startsWith('Bug') ? <BugRegular /> : <LightbulbRegular />;
  }

  return (
    <Popover open={open} onOpenChange={(_, d) => onOpenChange(d.open)} positioning="below-end" withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <Tooltip relationship="label" content="Benachrichtigungen">
            <Button
              appearance="transparent"
              aria-label={`Benachrichtigungen${data.unread ? ` (${data.unread} neu)` : ''}`}
              style={{ color: 'var(--colorNeutralForegroundOnBrand)' }}
              icon={<AlertRegular />}
            />
          </Tooltip>
          {data.unread > 0 ? (
            <CounterBadge
              count={data.unread}
              size="small"
              color="danger"
              style={{ position: 'absolute', top: 2, right: 2, pointerEvents: 'none' }}
            />
          ) : null}
        </div>
      </PopoverTrigger>
      <PopoverSurface style={{ padding: 0, width: 360, maxWidth: '90vw' }}>
        <div style={{ padding: '10px 14px', fontWeight: 600 }}>Benachrichtigungen</div>
        <Divider />
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {loading && data.items.length === 0 ? (
            <div style={{ padding: 16 }}><Spinner size="tiny" label="Lade…" /></div>
          ) : data.items.length === 0 ? (
            <div style={{ padding: 16, color: tokens.colorNeutralForeground3 }}>
              <Text>Keine Benachrichtigungen.</Text>
            </div>
          ) : (
            data.items.map((item, idx) => (
              <button
                key={`${item.kind}-${item.number ?? idx}`}
                type="button"
                onClick={() => activate(item)}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 14px',
                  border: 'none',
                  borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'inherit',
                }}
              >
                <span style={{ color: tokens.colorBrandForeground1, marginTop: 2 }}>{icon(item)}</span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Text weight="semibold" size={200} truncate wrap={false} style={{ flex: 1, minWidth: 0 }}>
                      {item.kind === 'issue' && item.number ? `#${item.number} ` : ''}{item.title}
                    </Text>
                    {item.kind === 'approval' ? (
                      <Badge appearance="tint" color="warning" size="small">Freigabe</Badge>
                    ) : item.state === 'closed' ? (
                      <Badge appearance="tint" color="success" size="small">geschlossen</Badge>
                    ) : null}
                  </span>
                  {item.subtitle ? (
                    <Text size={100} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                      {item.subtitle}
                    </Text>
                  ) : null}
                  <Text size={100} style={{ color: tokens.colorNeutralForeground4 }}>{ago(item.timestampUtc)}</Text>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
}
